import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Check, Loader2, Edit3, Save, X, Home } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { getCounties, getDriverProfile, updateDriverCounties } from '../services/api';

/**
 * Service Areas panel for the driver Profile tab.
 *
 * Lets drivers update which Irish counties they cover (unlimited) OR opt into
 * "anywhere in Ireland" coverage, and pick a single "main county" where they
 * are based. Only matched drivers receive push notifications when new jobs are
 * posted in their selected counties, so keeping this list current directly
 * drives job flow.
 *
 * Defensive UX:
 *   • Read-only summary by default; explicit "Edit" toggle reveals checkboxes.
 *   • "Anywhere in Ireland" is mutually exclusive with individual picks.
 *   • Main county must be one of the selected counties (auto-enforced).
 *   • Save shows inline spinner; Cancel reverts unsaved changes.
 */
export default function ServiceAreasPanel() {
  const [counties, setCounties] = useState([]);          // full list of valid IE counties
  const [selected, setSelected] = useState([]);          // driver's current picks
  const [servesAll, setServesAll] = useState(false);
  const [mainCounty, setMainCounty] = useState('');
  const [draftSelected, setDraftSelected] = useState([]);
  const [draftServesAll, setDraftServesAll] = useState(false);
  const [draftMainCounty, setDraftMainCounty] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const [{ data: counterList }, { data: profile }] = await Promise.all([
        getCounties(),
        getDriverProfile(),
      ]);
      setCounties(counterList || []);
      const list = Array.isArray(profile?.counties) ? profile.counties : [];
      const all = !!profile?.serves_all_ireland;
      const mc = profile?.main_county || '';
      setSelected(list);
      setServesAll(all);
      setMainCounty(mc);
      setDraftSelected(list);
      setDraftServesAll(all);
      setDraftMainCounty(mc);
    } catch (e) {
      // Silent — UI shows empty state which still allows the user to set it.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const summary = useMemo(() => {
    if (servesAll) return 'Available anywhere in Ireland (32 counties)';
    if (!selected.length) return 'No service areas set — you won\'t receive new-job alerts until you pick at least one county.';
    return selected.join(' · ');
  }, [selected, servesAll]);

  // When draft selection changes, ensure draft main_county stays valid.
  // - If "Anywhere in Ireland" is on, main_county can be any county.
  // - Otherwise main_county must be one of the selected counties (else clear it).
  const draftMainOptions = useMemo(() => {
    if (draftServesAll) return counties;
    return draftSelected;
  }, [draftServesAll, draftSelected, counties]);

  useEffect(() => {
    if (!editing) return;
    if (draftMainCounty && !draftMainOptions.includes(draftMainCounty)) {
      setDraftMainCounty('');
    }
  }, [draftMainOptions, draftMainCounty, editing]);

  const toggleCounty = (c) => {
    setDraftSelected((prev) => {
      if (prev.includes(c)) return prev.filter((x) => x !== c);
      return [...prev, c];
    });
    setDraftServesAll(false);
  };

  const startEdit = () => {
    setDraftSelected(selected);
    setDraftServesAll(servesAll);
    setDraftMainCounty(mainCounty);
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraftSelected(selected);
    setDraftServesAll(servesAll);
    setDraftMainCounty(mainCounty);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!draftServesAll && draftSelected.length === 0) {
      toast.error("Please pick at least 1 county or tick 'Anywhere in Ireland'.");
      return;
    }
    if (!draftMainCounty) {
      toast.error("Please choose your main county (where you're based).");
      return;
    }
    setSaving(true);
    try {
      const { data } = await updateDriverCounties({
        counties: draftServesAll ? [] : draftSelected,
        serves_all_ireland: draftServesAll,
        main_county: draftMainCounty,
      });
      setSelected(Array.isArray(data?.counties) ? data.counties : []);
      setServesAll(!!data?.serves_all_ireland);
      setMainCounty(data?.main_county || '');
      setEditing(false);
      toast.success('Service areas updated');
      window.dispatchEvent(new Event('jd:profile-updated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update service areas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-xl border border-slate-200/80 shadow-sm mb-6" data-testid="service-areas-panel">
      <CardContent className="py-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#1B5E20]" strokeWidth={1.75} />
            </span>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-[#050505] leading-tight font-['Inter']">
                Service areas
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Pick every county you can deliver in (no limit) — or cover all of Ireland. Then choose your main base county.
              </p>
            </div>
          </div>
          {!editing && !loading && (
            <Button
              variant="outline"
              size="sm"
              onClick={startEdit}
              className="gap-1.5 border-[#2E7D32]/30 text-[#1B5E20] hover:bg-[#2E7D32]/5 rounded-sm h-8"
              data-testid="edit-service-areas-btn"
            >
              <Edit3 className="w-3.5 h-3.5" /> Edit
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
        ) : !editing ? (
          <div className="space-y-3" data-testid="service-areas-summary">
            <div className="flex flex-wrap items-center gap-2">
              {servesAll ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2E7D32] text-white text-xs font-bold">
                  <Check className="w-3 h-3" /> Anywhere in Ireland
                </span>
              ) : selected.length === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-md font-medium">
                  {summary}
                </p>
              ) : (
                selected.map((c) => (
                  <span key={c} className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-[#1B5E20] text-xs font-bold border border-emerald-100" data-testid={`service-area-tag-${c}`}>
                    {c}
                  </span>
                ))
              )}
            </div>
            {mainCounty && (
              <div
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-md"
                data-testid="service-areas-main-county"
              >
                <Home className="w-3.5 h-3.5 text-[#F57C00]" /> Based in <span className="text-[#1B5E20]">{mainCounty}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="service-areas-editor">
            <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 bg-gray-50/60 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={draftServesAll}
                onChange={(e) => {
                  setDraftServesAll(e.target.checked);
                  if (e.target.checked) setDraftSelected([]);
                }}
                className="accent-[#2E7D32]"
                data-testid="service-areas-all-ireland"
              />
              <span className="text-sm font-semibold text-[#050505]">Anywhere in Ireland (all 32 counties)</span>
            </label>

            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">
                {draftServesAll
                  ? 'Disabled while "Anywhere in Ireland" is on'
                  : `Pick every county you cover — no limit (${draftSelected.length} selected)`}
              </p>
              <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 ${draftServesAll ? 'opacity-50 pointer-events-none' : ''}`}>
                {counties.map((c) => {
                  const checked = draftSelected.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCounty(c)}
                      className={`flex items-center justify-between gap-1 px-3 py-2 rounded-md border text-xs font-semibold transition-colors ${
                        checked
                          ? 'bg-[#2E7D32] text-white border-[#1B5E20]'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-[#2E7D32]'
                      }`}
                      data-testid={`service-area-county-${c}`}
                    >
                      <span className="truncate">{c}</span>
                      {checked && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Main county — single home base */}
            <div className="pt-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                <Home className="w-3.5 h-3.5 text-[#F57C00]" /> Main county <span className="text-red-500">*</span>
              </label>
              <p className="text-[11px] text-gray-500 mb-2">
                Where are you based? {draftServesAll ? 'Pick any Irish county.' : 'Must be one of the counties you cover above.'}
              </p>
              <select
                value={draftMainCounty}
                onChange={(e) => setDraftMainCounty(e.target.value)}
                disabled={draftMainOptions.length === 0}
                className="w-full sm:w-72 h-10 px-3 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30 focus:border-[#2E7D32] disabled:bg-gray-50 disabled:text-gray-400"
                data-testid="service-areas-main-county-select"
              >
                <option value="">Select a main county…</option>
                {draftMainOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 gap-2 bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-sm h-10"
                data-testid="save-service-areas-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save changes
              </Button>
              <Button
                variant="outline"
                onClick={cancelEdit}
                disabled={saving}
                className="gap-2 border-gray-300 rounded-sm h-10"
                data-testid="cancel-service-areas-btn"
              >
                <X className="w-4 h-4" /> Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
